import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { CoverageDeviceDebugApiService } from '../api/coverage-device-debug-api.service';
import { CoverageDeviceDebug } from '../api/models';

import {
	CoverageDeviceDebugSubscriptionGQL,
	DebugAudioFenceLobeSubscriptionGQL,
	GetCoverageDeviceDebugQueryGQL
} from './graphql/generated/device-coverage-device-debug.generated';
import { mapCoverageDeviceDebugFromSysApi } from './mappers/coverage-device-debug.mapper';

@Injectable()
export class SysApiCoverageDeviceDebugApiService implements CoverageDeviceDebugApiService {
	private readonly debugDevicesGqlSubscriptions: CombiningSubscriptionManager;
	private readonly debugAudioFenceLobesGqlSubscriptions: CombiningSubscriptionManager;

	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		subscriptionManagerService: SubscriptionManagerService,
		coverageDeviceSubscriptionGQL: CoverageDeviceDebugSubscriptionGQL,
		debugAudioFenceLobeSubscriptionGQL: DebugAudioFenceLobeSubscriptionGQL,
		private readonly getCoverageDebugDeviceQueryGQL: GetCoverageDeviceDebugQueryGQL
	) {
		this.logger = logger.createScopedLogger('SysApiCoverageDeviceDebugApiService');

		this.debugDevicesGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiCoverageDeviceDebugApiService:Device',
				changeTypes: [NodeChangeType.DeviceCoverageMode, NodeChangeType.DeviceCoveragePosition],
				subscriptionGql: coverageDeviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.debugAudioFenceLobesGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiCoverageDeviceDebugApiService:AudioFenceLobes',
				changeTypes: [NodeChangeType.DspDebugAudioFenceLobe],
				subscriptionGql: debugAudioFenceLobeSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDebugDevice$(deviceId: string): Observable<CoverageDeviceDebug> {
		const queryId = uuidv4();
		this.logger.trace('getDebugDevice$()', 'Querying for debug device', { queryId, deviceId });
		return this.getCoverageDebugDeviceQueryGQL
			.watch({ variables: { id: deviceId }, errorPolicy: 'all' })
			.valueChanges.pipe(
				waitForGQLCompleteQueryOrThrow(),
				map((query) => {
					if (!query.data?.node || !('isDeviceNode' in query.data.node)) {
						throw ApolloQueryErrorMapper.getError(query);
					}

					const fragment = query.data.node as Parameters<typeof mapCoverageDeviceDebugFromSysApi>[0];

					const device = mapCoverageDeviceDebugFromSysApi(fragment);
					this.logger.trace('getDebugDevice$()', 'Query updated', { queryId, device });
					return device;
				}),
				tapOnce((device) => {
					this.debugDevicesGqlSubscriptions.subscribe(queryId, device.id);
					const fencedLobeIds = device.features.dspDebugAudioFenceLobes?.value?.map((lobe) => lobe.id) ?? [];
					this.debugAudioFenceLobesGqlSubscriptions.subscribeToOnly(queryId, fencedLobeIds);
				}),
				noMoreSubscribers(() => {
					this.debugDevicesGqlSubscriptions.unsubscribeQuery(queryId);
					this.debugAudioFenceLobesGqlSubscriptions.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					this.logger.error('getDebugDevice$()', 'Query failed', { queryId, deviceId, error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}
}
